import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, storage } from '../../config/firebase';

const ADMIN_ROLES = new Set(['super_admin', 'superadmin']);
const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
const VIDEO_TYPES = new Set(['video/mp4', 'video/webm', 'video/quicktime']);
const DOCUMENT_TYPES = new Set([
  'application/pdf','text/plain','text/csv','application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint','application/vnd.openxmlformats-officedocument.presentationml.presentation','application/zip',
]);
const GENERAL_TYPES = new Set([...IMAGE_TYPES, ...VIDEO_TYPES, ...DOCUMENT_TYPES]);
const safeSegment = (value = '') => String(value || '').replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 120);
const safeName = (value = 'file') => String(value || 'file').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(-140) || 'file';
const safeFolder = (value='uploads') => String(value || 'uploads').split('/').map(safeSegment).filter(Boolean).join('/') || 'uploads';

export const ADMIN_UPLOAD_LIMITS = Object.freeze({ image: 5 * 1024 * 1024, video: 50 * 1024 * 1024, document: 15 * 1024 * 1024, default: 15 * 1024 * 1024 });

export async function assertPlatformAdmin() {
  const current = auth.currentUser;
  if (!current?.uid) throw new Error('Please sign in with an administrator account.');
  const snap = await getDoc(doc(db, 'users', current.uid));
  if (!snap.exists()) throw new Error('This account has no administrator profile.');
  const profile = snap.data() || {};
  const role = String(profile.role || profile.userRole || '').toLowerCase().replace(/[\s-]+/g, '_');
  if (!ADMIN_ROLES.has(role) || profile.isActive === false || profile.isBlocked === true) throw new Error('Administrator permission is required for this upload.');
  return { user: current, profile, role };
}

export function validateAdminUpload(file, { kind = 'default', maxBytes, allowedTypes } = {}) {
  if (!file) throw new Error('A file is required.');
  const type = String(file.type || '').toLowerCase();
  const size = Number(file.size || 0);
  const set = allowedTypes ? new Set(allowedTypes.map((v)=>String(v).toLowerCase())) : kind==='image'?IMAGE_TYPES:kind==='video'?VIDEO_TYPES:kind==='document'?DOCUMENT_TYPES:GENERAL_TYPES;
  if (!set.has(type)) throw new Error(kind === 'image' ? 'Choose a PNG, JPG/JPEG or WEBP image.' : kind === 'video' ? 'Choose an MP4, WEBM or MOV video.' : 'Choose a supported file type.');
  const limit = Number(maxBytes || ADMIN_UPLOAD_LIMITS[kind] || ADMIN_UPLOAD_LIMITS.default);
  if (size <= 0) throw new Error('The selected file is empty.');
  if (size > limit) throw new Error(`File is too large. Maximum size is ${Math.round(limit / (1024 * 1024))} MB.`);
  return true;
}

export async function getPlatformStorageProvider() {
  const fallback = { provider:'firebase', firebase:{basePath:'companies'}, cloudinary:{cloudName:'',uploadPreset:'',folder:'telcofair'} };
  try {
    const snap = await getDoc(doc(db,'systemSettings','public_runtime'));
    const runtime = snap.exists() ? snap.data() || {} : {};
    const cfg = runtime?.storage && typeof runtime.storage === 'object' ? runtime.storage : {};
    const provider = ['firebase','cloudinary'].includes(String(cfg.provider||'').toLowerCase()) ? String(cfg.provider).toLowerCase() : 'firebase';
    return { ...fallback, ...cfg, provider, firebase:{basePath:'companies'}, cloudinary:{...fallback.cloudinary,...(cfg.cloudinary||{})} };
  } catch (error) {
    console.warn('Platform storage runtime unavailable; Firebase Storage fallback is active:', error?.message || error);
    return fallback;
  }
}

const scopeRoot = ({ scope, companyId, userId }) => {
  if (scope === 'platform') return 'platform';
  if (scope === 'company') {
    const id = safeSegment(companyId); if (!id) throw new Error('companyId is required for a company upload.');
    return `companies/${id}`;
  }
  if (scope === 'user') {
    const id = safeSegment(userId); if (!id) throw new Error('userId is required for a user upload.');
    return `users/${id}`;
  }
  throw new Error('Unsupported upload scope.');
};

async function uploadFirebase({ file, scope, companyId, userId, folder, onProgress, adminUid }) {
  const root = scopeRoot({scope,companyId,userId});
  const path = `${root}/${safeFolder(folder)}/${Date.now()}_${safeName(file.name)}`;
  const fileRef = ref(storage, path);
  const task = uploadBytesResumable(fileRef, file, { contentType:file.type||undefined, customMetadata:{scope,uploadedBy:adminUid,...(companyId?{companyId:String(companyId)}:{}),...(userId?{userId:String(userId)}:{})} });
  await new Promise((resolve,reject)=>task.on('state_changed',(snap)=>onProgress?.(Math.round((snap.bytesTransferred/Math.max(snap.totalBytes,1))*100)),reject,resolve));
  return { provider:'firebase',scope,companyId:companyId||null,userId:userId||null,url:await getDownloadURL(task.snapshot.ref),path,fullPath:task.snapshot.ref.fullPath,contentType:file.type||'',bytes:file.size||0 };
}

async function uploadCloudinary({ file, scope, companyId, userId, folder, onProgress, config }) {
  const cloudName = String(config?.cloudinary?.cloudName || '').trim();
  const uploadPreset = String(config?.cloudinary?.uploadPreset || '').trim();
  const rootFolder = safeFolder(config?.cloudinary?.folder || 'telcofair');
  const scopedRoot = scopeRoot({ scope, companyId, userId });
  const targetFolder = [rootFolder, scopedRoot, safeFolder(folder)].filter(Boolean).join('/');
  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary unsigned upload requires Cloud Name and an unsigned Upload Preset in Super Admin Settings.');
  }

  const form = new FormData();
  form.append('file', file);
  form.append('upload_preset', uploadPreset);
  form.append('folder', targetFolder);

  const xhr = new XMLHttpRequest();
  const result = await new Promise((resolve, reject) => {
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${encodeURIComponent(cloudName)}/auto/upload`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || '{}');
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data?.error?.message || 'Cloudinary unsigned upload failed.'));
      } catch (error) { reject(error); }
    };
    xhr.onerror = () => reject(new Error('Cloudinary upload network error. Check the Cloud Name, unsigned preset and browser/network policy.'));
    xhr.send(form);
  });

  return {
    provider: 'cloudinary', scope, companyId: companyId || null, userId: userId || null,
    url: result.secure_url, publicId: result.public_id, resourceType: result.resource_type,
    width: result.width, height: result.height, bytes: result.bytes, format: result.format,
    cloudName,
  };
}

export async function uploadAdminFile({ file, scope = 'platform', companyId, userId, folder = 'uploads', kind = 'default', maxBytes, allowedTypes, onProgress }) {
  const { user } = await assertPlatformAdmin();
  validateAdminUpload(file,{kind,maxBytes,allowedTypes});
  scopeRoot({scope,companyId,userId});
  const config = await getPlatformStorageProvider();
  if (config.provider === 'cloudinary') return uploadCloudinary({file,scope,companyId,userId,folder,onProgress,config});
  return uploadFirebase({file,scope,companyId,userId,folder,onProgress,adminUid:user.uid});
}

export async function deleteAdminFile({ asset, scope='platform', companyId, userId }) {
  await assertPlatformAdmin();
  if (!asset) return {skipped:true};
  if (asset.provider === 'cloudinary') {
    // Cloudinary unsigned uploads cannot request return_delete_token.
    // Deleting a Cloudinary asset requires a signed server-side request using
    // the API secret, so the browser deliberately does not attempt deletion.
    return { skipped: true, reason: 'cloudinary_unsigned_requires_server_delete' };
  }
  const path = String(asset.fullPath||asset.path||'');
  if (!path) return {skipped:true};
  const root = `${scopeRoot({scope,companyId,userId})}/`;
  if (!path.startsWith(root)) throw new Error('Refusing to delete a file outside the selected administrator scope.');
  await deleteObject(ref(storage,path));
  return {success:true};
}

export default { uploadAdminFile, deleteAdminFile, validateAdminUpload, assertPlatformAdmin, getPlatformStorageProvider, ADMIN_UPLOAD_LIMITS };
